import { Loan, MainResidenceDetails } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { MainResidenceForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/MainResidence/components/MainResidenceForm';
import {
  MainResidenceFormSchema,
  MainResidenceFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/MainResidence/schemas/MainResidenceFormSchema';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { AddressMap } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/maps/address.map';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { removeNullKeysFromObject } from '@/utils/removeNullKeysFromObject';

export const MainResidenceEditDialog = ({
  currentLoan,
  mainResidenceDetails,
  opened,
  setOpened,
  loading,
  toggleDialog,
  updateBorrowerContract,
}: {
  currentLoan: Loan;
  mainResidenceDetails: MainResidenceDetails;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  toggleDialog: () => void;
  updateBorrowerContract?: (data) => void;
}) => {
  const mainResidenceDefaultValues = {
    ownershipType: mainResidenceDetails?.ownershipDetails.ownershipType,
    propertyType: mainResidenceDetails?.ownershipDetails.propertyType,
    homeExpenses: mainResidenceDetails?.homeExpenses.amount.toString(),
    entranceDate: mainResidenceDetails?.entranceDate || new Date(),
    mortgage: mainResidenceDetails?.ownershipDetails.mortgage?.toString() ?? null,
    mainResidenceAddress: AddressMap.toForm(mainResidenceDetails.address),
  };

  const mainResidencePreMapper = (useFormValues: MainResidenceFormType) => {
    return {
      ownershipDetails: {
        ownershipType: useFormValues.ownershipType,
        propertyType: useFormValues.propertyType ?? null,
        mortgage: useFormValues.mortgage === 'true' ? true : false,
      },
      homeExpenses: {
        currency: currentLoan?.loanConditions.currency,
        amount: parseFloat(useFormValues.homeExpenses),
      },
      address: AddressMap.toDto(useFormValues.mainResidenceAddress),
      entranceDate: useFormValues.entranceDate,
    };
  };

  const onSubmitForm = async (useFormValues: MainResidenceFormType) => {
    if (updateBorrowerContract) {
      const preMappedMainResidenceValues = removeNullKeysFromObject(mainResidencePreMapper(useFormValues));
      await updateBorrowerContract({
        userDetails: {
          mainResidenceDetails: preMappedMainResidenceValues,
        },
      });
    }
    toggleDialog();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_MAIN_RESIDENCE_INFORMATION'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      defaultValues={mainResidenceDefaultValues}
      formSchema={(t) => MainResidenceFormSchema(t)}
    >
      <MainResidenceForm
        mainResidenceDetails={mainResidenceDetails}
        fullWidth={true}
        fieldVariant={FieldVariant.form}
      />
    </EditDialogWrapper>
  );
};
